import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

type ExplorerNotesPopupProps = {
  open: boolean;
  onClose: () => void;
};

type ExplorerNoteSubsection = {
  id: string;
  title: string;
  content: React.ReactNode;
};

type ExplorerNoteSection = {
  id: string;
  title: string;
  content?: React.ReactNode;
  subsections?: ExplorerNoteSubsection[];
};

const sections: ExplorerNoteSection[] = [
  {
    id: "why",
    title: "Why this exists",
    content: (
      <>
        <Box sx={{ mb: 1.5 }}>
          This project started off based on some initial encouraging explorations of combining AI with short clips, independent of PBVision.

          I found that chatgpt could make use of the video and produce voluminous responses for technique and drills associated with improving the technique.

          I mean, it was overwhelming the amount of (useful) information you could get from just a 2-second clip with AI.

          So, this proved that something could work, although the next steps were not clear.

          I reached out to the PB Vision team to see if they were interested.  Mild interest, acknowledging the surprising amount of information you can get for such a short clip, but things were too embryonic to go much further in any sort of formal collaboration with them.

          However,this opened my eyes to looking at the raw data that PB Vision currently provides for a recorded game.  

          I tried some experiments with overlaying my own boxes using [cv] to see if I could get information on trajectories for the purpose of estimating spin (based on fitting the magnus-force equation to the trajectory) - in the end, I decided it was too hard and was not going to be reliable, and it's not even clear that the information about spin is useful or interesting.








        </Box>

        <Box>
          It is not meant to be polished. It is closer to a field notebook
          embedded inside the app.
        </Box>
      </>
    ),
    subsections: [
      {
        id: "why-origin",
        title: "Origin of this project",
        content: (
          <>
            <Box sx={{ mb: 1.5 }}>foo1</Box>
            <Box>foo2</Box>
          </>
        ),
      },
      {
        id: "why-format",
        title: "Why put this inside the app",
        content: (
          <>
            <Box sx={{ mb: 1.5 }}>
              dummy subsection text about why this may live inside the app
              rather than in a normal blog.
            </Box>
            <Box>
              More dummy text here for structure testing and layout.
            </Box>
          </>
        ),
      },
    ],
  },
  {
    id: "triage",
    title: "Triage",
    content: (
      <>
        <Box sx={{ mb: 1.5 }}>
          The triage view became one of the clearest entry points into the game.
        </Box>
        <Box>
          It is less about being perfectly right than about quickly surfacing
          rallies worth examining first.
        </Box>
      </>
    ),
    subsections: [
      {
        id: "triage-entry",
        title: "Why it became the entry point",
        content: (
          <>
            <Box sx={{ mb: 1.5 }}>
              dummy subsection text about triage as a front door into the app.
            </Box>
            <Box>
              More placeholder notes for later expansion.
            </Box>
          </>
        ),
      },
      {
        id: "triage-limits",
        title: "What triage is not",
        content: (
          <>
            <Box sx={{ mb: 1.5 }}>
              dummy subsection text about limitations and non-goals.
            </Box>
            <Box>
              Another paragraph placeholder.
            </Box>
          </>
        ),
      },
    ],
  },
  {
    id: "limitations",
    title: "Limitations / frustrations",
    content: (
      <>
        <Box sx={{ mb: 1.5 }}>
          Some things that felt promising in theory turned out not to matter
          much once actually seen in the interface.
        </Box>
        <Box>
          Other things became much more interesting only after watching the app
          in use.
        </Box>
      </>
    ),
    subsections: [
      {
        id: "limitations-false-starts",
        title: "False starts",
        content: (
          <>
            <Box sx={{ mb: 1.5 }}>
              dummy subsection text about things that seemed promising but
              weren’t.
            </Box>
            <Box>More placeholder text here.</Box>
          </>
        ),
      },
      {
        id: "limitations-ui",
        title: "Interface frustrations",
        content: (
          <>
            <Box sx={{ mb: 1.5 }}>
              dummy subsection text about interface complexity and tension.
            </Box>
            <Box>More placeholder text here.</Box>
          </>
        ),
      },
    ],
  },
  {
    id: "ai",
    title: "AI philosophy",
    content: (
      <>
        <Box sx={{ mb: 1.5 }}>
          AI feels most useful here as a contextual, optional assistant rather
          than the main point of the product.
        </Box>
        <Box>
          The core value is still in the structure, the video, and the ability
          to move around evidence quickly.
        </Box>
      </>
    ),
    subsections: [
      {
        id: "ai-role",
        title: "What AI is doing here",
        content: (
          <>
            <Box sx={{ mb: 1.5 }}>
              dummy subsection text about AI as a helper rather than the main
              attraction.
            </Box>
            <Box>More placeholder text here.</Box>
          </>
        ),
      },
      {
        id: "ai-non-goals",
        title: "What I do not want AI to become",
        content: (
          <>
            <Box sx={{ mb: 1.5 }}>
              dummy subsection text about non-goals and boundaries.
            </Box>
            <Box>More placeholder text here.</Box>
          </>
        ),
      },
    ],
  },
];

export function ExplorerNotesPopup({
  open,
  onClose,
}: ExplorerNotesPopupProps) {
  const handleJumpToId = (id: string) => {
    const el = document.getElementById(`explorer-notes-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleBackToTop = () => {
    const scroller = document.getElementById("explorer-notes-scroll");
    if (scroller) {
      scroller.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#111111",
          color: "#ffffff",
          border: "1px solid #2f2f2f",
          boxShadow: "0 18px 60px #000000",
          backgroundImage: "none",
          minHeight: "72vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontWeight: 700,
          fontSize: 22,
          letterSpacing: "0.01em",
          color: "#ffffff",
          borderBottom: "1px solid #242424",
        }}
      >
        Explorer Notes
      </DialogTitle>

      <DialogContent
        id="explorer-notes-scroll"
        dividers={false}
        sx={{
          pt: 2,
          px: 2.5,
          pb: 2.5,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.03em",
              color: "#d8d8d8",
              mb: 1.25,
            }}
          >
            TABLE OF CONTENTS
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {sections.map((section) => (
              <Box key={section.id}>
                <Button
                  onClick={() => handleJumpToId(section.id)}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    color: "#f3f3f3",
                    border: "1px solid #353535",
                    backgroundColor: "#202020",
                    px: 1.25,
                    py: 0.85,
                    borderRadius: 1,
                    fontSize: 16,
                    width: "100%",
                    "&:hover": {
                      backgroundColor: "#272727",
                      borderColor: "#4a4a4a",
                    },
                  }}
                >
                  {section.title}
                </Button>

                {section.subsections && section.subsections.length > 0 ? (
                  <Box
                    sx={{
                      mt: 0.75,
                      ml: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    {section.subsections.map((subsection) => (
                      <Button
                        key={subsection.id}
                        onClick={() => handleJumpToId(subsection.id)}
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          color: "#dcdcdc",
                          border: "1px solid #2f2f2f",
                          backgroundColor: "#181818",
                          px: 1.1,
                          py: 0.65,
                          borderRadius: 1,
                          fontSize: 14,
                          width: "100%",
                          "&:hover": {
                            backgroundColor: "#202020",
                            borderColor: "#404040",
                          },
                        }}
                      >
                        {subsection.title}
                      </Button>
                    ))}
                  </Box>
                ) : null}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {sections.map((section) => (
            <Box
              key={section.id}
              id={`explorer-notes-${section.id}`}
              sx={{
                borderTop: "1px solid #242424",
                pt: 2.5,
              }}
            >
              <Box
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 1.15,
                  mb: 1.5,
                  color: "#ffffff",
                }}
              >
                {section.title}
              </Box>

              {section.content ? (
                <Box
                  sx={{
                    fontSize: 17,
                    lineHeight: 1.7,
                    color: "#f3f3f3",
                    mb: section.subsections?.length ? 2.5 : 0,
                  }}
                >
                  {section.content}
                </Box>
              ) : null}

              {section.subsections?.map((subsection) => (
                <Box
                  key={subsection.id}
                  id={`explorer-notes-${subsection.id}`}
                  sx={{
                    mt: 2.5,
                    pl: 1.5,
                    borderLeft: "2px solid #2c2c2c",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 19,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      mb: 1,
                      color: "#f5f5f5",
                    }}
                  >
                    {subsection.title}
                  </Box>

                  <Box
                    sx={{
                      fontSize: 16,
                      lineHeight: 1.7,
                      color: "#f3f3f3",
                    }}
                  >
                    {subsection.content}
                  </Box>
                </Box>
              ))}

              <Box sx={{ mt: 1.5 }}>
                <Button
                  onClick={handleBackToTop}
                  sx={{
                    textTransform: "none",
                    px: 0,
                    minWidth: 0,
                    color: "#bdbdbd",
                    fontSize: 14,
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Back to top
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          pb: 2,
          pt: 1,
          borderTop: "1px solid #242424",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            px: 1.4,
            border: "1px solid #4a4a4a",
            backgroundColor: "#1d1d1d",
            color: "#f3f3f3",
            "&:hover": {
              backgroundColor: "#262626",
              borderColor: "#5a5a5a",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}